<template>
  <div class="import-progress-indicator" v-if="hasActiveTask">
    <div class="progress-content">
      <div class="progress-info">
        <LinkflareLoader inline :aria-label="$t('importData.progress.importing')" size="1rem" />
        <span class="progress-text">{{ $t('importData.progress.importing') }}</span>
        <span class="progress-percent">{{ progressPercent }}%</span>
      </div>
      <div class="progress-bar-container">
        <div class="progress-bar" :style="{ width: progressPercent + '%' }"></div>
      </div>
      <div class="progress-details">
        <span class="urls-imported">{{ $t('importData.progress.urlCount', { done: urlsImported, total: currentTask.urls_found }) }}</span>
        <span class="time-remaining" v-if="estimatedTimeRemaining > 0">
          {{ $t('importData.progress.timeRemaining', { time: formatTime(estimatedTimeRemaining) }) }}
        </span>
      </div>
    </div>
    <Button 
      icon="pi pi-times"
      class="p-button-text p-button-sm close-btn"
      @click="$emit('cancel')"
      v-tooltip.bottom="$t('importData.progress.dismissTooltip')"
    />
  </div>
</template>

<script>
import { computed } from 'vue'
import Button from 'primevue/button'
import LinkflareLoader from './LinkflareLoader.vue'

export default {
  name: 'ImportProgressIndicator',
  components: {
    Button,
    LinkflareLoader
  },
  props: {
    currentTask: {
      type: Object,
      default: null
    },
    progressPercent: {
      type: Number,
      default: 0
    },
    urlsImported: {
      type: Number,
      default: 0
    },
    estimatedTimeRemaining: {
      type: Number,
      default: 0
    }
  },
  emits: ['cancel'],
  setup(props) {
    const hasActiveTask = computed(() => props.currentTask !== null)

    const formatTime = (seconds) => {
      if (seconds < 60) return `${seconds}s`
      const totalMinutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      
      if (totalMinutes >= 60) {
        const hours = Math.floor(totalMinutes / 60)
        const mins = totalMinutes % 60
        if (mins === 0) return `${hours}h`
        return `${hours}h ${mins}m`
      }
      
      if (secs === 0) return `${totalMinutes}m`
      return `${totalMinutes}m ${secs}s`
    }

    return {
      hasActiveTask,
      formatTime
    }
  }
}
</script>

<style scoped>
.import-progress-indicator {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--accent-light);
  border-bottom: 1px solid var(--border-color);
  position: relative;
}

.progress-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.progress-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.progress-text {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.progress-percent {
  margin-left: auto;
  font-weight: 600;
  color: var(--accent-color);
  font-size: 0.9rem;
}

.progress-bar-container {
  width: 100%;
  height: 4px;
  background: var(--bg-secondary);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: var(--accent-color);
  transition: width 0.3s ease;
  border-radius: 2px;
}

.progress-details {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.urls-imported {
  font-weight: 500;
}

.time-remaining {
  margin-left: auto;
}

.close-btn {
  color: var(--text-secondary) !important;
  padding: 0.25rem !important;
}

.close-btn:hover {
  color: var(--text-primary) !important;
  background: var(--bg-secondary) !important;
}

@media (max-width: 768px) {
  .import-progress-indicator {
    padding: 0.5rem 0.75rem;
  }

  .progress-text {
    font-size: 0.85rem;
  }

  .progress-percent {
    font-size: 0.85rem;
  }

  .progress-details {
    font-size: 0.75rem;
    flex-wrap: wrap;
  }

  .time-remaining {
    margin-left: 0;
    width: 100%;
  }
}
</style>
